import {
  Box,
  dialogTitleClasses,
  iconButtonClasses,
  outlinedInputClasses,
  Paper,
  Select,
  styled,
  Typography
} from '@mui/material';

import { Dialog } from 'components/shared';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';

import type { ElementType } from 'react';

export const Container = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
}));

/** The grid's own `Toolbar` justifies to the end, so claim the row and start at the left. */
export const ToolbarRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  // room below so the dropdown clears the Period column header it sits above
  padding: theme.spacing(1, 0, 2),
  // small nudge right so the year picker lines up over the Period column
  paddingLeft: 5
}));

/** Column picker and export stay at the right edge, where every other grid keeps them. */
export const ToolbarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: 'auto'
}));

/**
 * Borrows the outlined-button look — primary border, primary text, same height — so the year picker
 * sits level with the Recalculate / Year corrections buttons on the other end of the toolbar.
 */
export const YearSelect = styled(Select)(({ theme }) => ({
  minWidth: theme.spacing(11),

  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.primary.main
  },
  [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.primary.dark
  },
  [`& .${outlinedInputClasses.input}`]: {
    padding: theme.spacing(0.5, 1.5),
    color: theme.palette.primary.main
  }
}));

/**
 * Colours the balance value. Dojo set the literal colours 'green' and 'red' inline
 * (`EmployeeWorkAgreementsGridColumns.js:66,72`); these come from the theme instead.
 */
export const BalanceValue = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone'
})<{ tone?: 'success' | 'error' }>(({ theme, tone }) => ({
  ...(tone && { color: theme.palette[tone].main })
}));

/**
 * The WAB warning/alert marker, replacing the `.wabWarning` / `.wabAlert` cell classes from
 * `EmployeeWorkAgreementsHours.less:93-127`.
 */
export const WabDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone'
})<{ tone: 'warning' | 'error' }>(({ theme, tone }) => ({
  display: 'inline-block',
  width: theme.spacing(1),
  height: theme.spacing(1),
  marginLeft: theme.spacing(0.5),
  borderRadius: '50%',
  verticalAlign: 'middle',
  backgroundColor: theme.palette[tone].main
}));

export const TotalsLabel = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold
}));

/**
 * The month window. The shared dialog's header is sized for a page title; this one carries a month
 * and two arrows, so it gets tighter padding, a smaller title and a smaller close button.
 */
export const DetailDialog = styled(Dialog)(({ theme }) => ({
  [`& .${dialogTitleClasses.root}`]: {
    ...theme.typography.subtitle1,
    padding: theme.spacing(1, 1, 1, 2),

    [`& .${iconButtonClasses.root}`]: {
      padding: theme.spacing(0.5)
    }
  }
}));

/** A correction input inside a detail table cell: no label, right-aligned like the value beside it. */
export const CorrectionField = styled(ControlledNumericField)(({ theme }) => ({
  // room for five characters plus the number spinner: '12,24' must not be clipped
  width: theme.spacing(14),

  [`& .${outlinedInputClasses.input}`]: {
    padding: theme.spacing(0.5, 1),
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  }
}));

export const DetailPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 0),
  backgroundColor: theme.palette.background.default
}));

export const DetailPanelHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end'
}));

/**
 * One group of values as an outlined card, the shape the window has had all along.
 * `component` needs re-declaring: styled() drops Paper's polymorphic prop typing.
 * Drag handle and remove button appear on hover, as on the home-page tiles.
 */
export const DetailGroupBlock = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isDragging'
})<{ component?: ElementType; isDragging?: boolean }>(
  ({ theme, isDragging }) => ({
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    ...(isDragging && {
      opacity: 0.9,
      zIndex: theme.zIndex.tooltip,
      boxShadow: theme.shadows[3]
    }),

    '&:hover .detail-table-tools, &:focus-within .detail-table-tools': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  })
);

export const DetailGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  borderBottom: `2px solid ${theme.palette.primary.main}`
}));

/** The picker dialog's switch list; nested for the per-section sub-lists. */
export const PickerRows = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5)
}));

export const PickerRow = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingLeft: theme.spacing(2)
}));

/**
 * Transparent until the table is hovered or focused. Deliberately not `visibility: hidden`: that
 * drops the buttons out of the accessibility tree, so keyboard and screen-reader users lose them.
 */
export const DetailTableTools = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(0.5),
  opacity: 0,
  pointerEvents: 'none',
  transition: theme.transitions.create('opacity')
}));

export const DetailGroupTitle = styled('h4')(({ theme }) => ({
  ...theme.typography.subtitle2,
  margin: 0,
  padding: theme.spacing(1, 1.5)
}));

/** One stacked block per section: its heading, then its group blocks. */
export const DetailSectionBlock = styled(Box)<{ component?: ElementType }>(
  ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  })
);

export const DetailSectionHeading = styled('h3')(({ theme }) => ({
  ...theme.typography.subtitle1,
  fontWeight: theme.typography.fontWeightBold,
  margin: 0
}));

/** The memo and the salary trigger, the only corrections with no value column to sit in. */
export const MemoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,

  '& > :first-of-type': {
    flex: 1
  }
}));

/** The group cards of one section, side by side; they only wrap when the window gets narrow. */
export const DetailGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing(2),

  '& > *': {
    flex: '1 1 280px'
  }
}));
